import { assign, find, get, has, replace, set, isEmpty } from 'lodash';
import {
  OperationalState,
  AdministrativeState,
  RunType,
  TestType,
  ITestList,
  TestTypeValue,
  ITestListData,
  TestTypeDisplayValue,
  ICustomer,
  IMetaDataItem,
  TestModeLabel,
  TestLayerLabel,
  TestLayerValue
} from './types';
import { IEntity } from '@nrwl/common-lib';
import { Device } from '../../providers/device/device';

export class TestListEntity implements IEntity {
  isDeleted: boolean;
  testListData: ITestList;
  escapedName: string;
  originatorAgent: Device;
  destinationAgent: Device;

  constructor(dataIn: ITestList) {
    this.escapedName = this.escapeString(dataIn.name);
    this.testListData = <ITestList>{};
    this.setTestList(dataIn);
    this.originatorAgent = undefined;
    this.destinationAgent = undefined;
    this.isDeleted = false;
  }

  //-------------------------------------------------Monitor data-------------------------------------------------------
  getTestList(): ITestList {
    return this.testListData;
  }

  getId(): string {
    return this.testListData.id || null;
  }

  getMAId(): TestType {
    return get(this.testListData, 'MaId', null);
  }

  getPhyId(): string {
    return get(this.testListData, 'phyId', '');
  }

  getName(): string {
    return this.testListData.name || null;
  }

  getEscapedName(): string {
    return this.escapedName;
  }

  escapeString(input: string): string {
    return JSON.stringify(input).replace(/^"(.*)"$/, '$1');
  }

  getAlias(): string {
    return this.testListData.name || null;
  }

  getTestListData(): ITestListData {
    return typeof this.testListData['test-list-data'] === 'string' &&
      this.testListData['test-list-data'] !== 'none'
      ? JSON.parse(this.testListData['test-list-data'])
      : {};
  }

  getModel(): string {
    return this.testListData.model || '';
  }

  getAgentMode(): string {
    return this.getTestListData()?.mode || '';
  }

  getSecondayAgentModel(): string {
    return this.getSecondaryAgent()?.model || '';
  }

  getModelLabel(): string {
    return this.getModel();
  }

  getSecondaryModelLabel(): string {
    if (this.getSecondayAgentModel()) {
      return this.getSecondayAgentModel();
    }
    return null;
  }

  getDestinationApplication(): any {
    const tpInfo = this.getTestListData().tpInfo;
    if (tpInfo?.length) {
      return tpInfo.find(data => data.model === 'application');
    }
    return null;
  }

  getDestinationApplicationId(): string {
    return this.getDestinationApplication()?.id;
  }

  getDestinationApplicationName(): string {
    return this.getDestinationApplication()?.name || '';
  }

  getDestinationApplicationUrl(): string {
    return this.getDestinationApplication()?.url || '';
  }

  getLayer(): string {
    return this.getTestListData().layer || '';
  }

  getCustomer(): ICustomer {
    return this.getTestListData().customer || null;
  }

  getMode(): string {
    return this.getTestListData().mode || '';
  }

  getVendor(): string {
    return this.testListData.vendor;
  }

  getVersion(): string {
    return this.testListData.version || '';
  }

  getRunId(): number {
    return this.testListData.runId;
  }

  getTestStatus(): OperationalState {
    return this.testListData.status || null;
  }

  getTestStateMessage(): string {
    return this.testListData.msg || null;
  }

  getExpirationDate(): string {
    return this.testListData['testExpTime'];
  }

  getAdministrativeState(): AdministrativeState {
    return this.testListData.administrativeState || null;
  }

  getUpdatedTime(): string {
    return this.testListData.lastUpdateTime || '';
  }

  getTestListRunType(): RunType {
    return this.testListData.runType || '';
  }

  getTestMessage(): string {
    return this.testListData.msg || '';
  }

  getTestConfig(): string {
    return this.testListData.testConfig || '';
  }

  isRunOnceTest(): boolean {
    return this.getTestListRunType() === 'on-demand';
  }

  setTestList(dataIn: ITestList): void {
    assign(this.testListData, dataIn);
    this.escapedName = this.escapeString(this.testListData.name);
  }

  getSipProxyOriginator(): IMetaDataItem {
    const testListData =
      typeof this.testListData['test-list-data'] === 'string' &&
      this.testListData['test-list-data'] !== 'none'
        ? JSON.parse(this.testListData['test-list-data'])
        : null;
    return find(testListData.proxyConfig, { key: 'sip-proxy-originator' });
  }

  getSipProxyDestination(): IMetaDataItem {
    const testListData =
      typeof this.testListData['test-list-data'] === 'string' &&
      this.testListData['test-list-data'] !== 'none'
        ? JSON.parse(this.testListData['test-list-data'])
        : null;
    return find(testListData.proxyConfig, { key: 'sip-proxy-destination' });
  }

  getSIP(): string {
    return this.destinationAgent?.getName() || '';
  }

  hasAnySipProxy(): boolean {
    const sipProxyInfo = this.getSipProxyOriginator() || this.getSipProxyDestination();

    if (sipProxyInfo && sipProxyInfo.value) {
      const proxy = JSON.parse(sipProxyInfo.value);

      return has(proxy['sip-proxy'], 'proxy-server-config');
    }

    return false;
  }

  setName(name: string): void {
    set(this.testListData, 'name', name);
  }

  setTestStatus(state: OperationalState): void {
    set(this.testListData, 'status', state);
  }

  setAdministrativeState(state: AdministrativeState): void {
    set(this.testListData, 'administrativeState', state);
  }

  setTestStateMessage(message: string): void {
    set(this.testListData, 'msg', message);
  }

  getOriginatorAgentId(): string {
    return this.originatorAgent?.getId() || null;
  }

  getOriginatorAgentAlias(): string {
    return this.getMAId() || '';
  }

  getDestinationAgentId(): string {
    return this.destinationAgent?.getId() || null;
  }

  getDestinationAgentAlias(): string {
    if (this.isAgentBased()) {
      return this.testListData.secondary?.[0]?.MaId || '';
    }
    return this.getDestinationApplicationName();
  }

  getSecondaryAgent(): ITestList {
    return this.testListData.secondary?.[0] || null;
  }

  getDestinationPhyId(): string {
    return get(this.testListData.secondary?.[0], 'phyId', '');
  }

  getTopId(): string {
    return this.testListData.topId || null;
  }

  //----------------------------------------------------Agent data--------------------------------------------------------
  getOriginatorAgent(): Device {
    return this.originatorAgent;
  }

  setOriginatorAgent(agent: Device): void {
    this.originatorAgent = agent;
  }

  //--------------------------------------------Destination agent data--------------------------------------------------
  getDestinationAgent(): Device {
    return this.destinationAgent || null;
  }

  setDestinationAgent(agent: Device): void {
    this.destinationAgent = agent;
  }

  getDestinationAlias(): string {
    return this.changeDestroyMsg(get(this.destinationAgent, 'agentSelector.alias', ''));
  }

  //---------------------------------------------------Status-----------------------------------------------------------
  isEnabled(): boolean {
    return this.getAdministrativeState() === 'Enabled';
  }

  isObserved(): boolean {
    return this.getTestStatus() === 'running';
  }

  isAgentBased(): boolean {
    return (<Array<string>>['servicecontinuity', 'multistep']).indexOf(this.getTestType()) === -1;
  }

  //----------------------------------------------------Tools-------------------------------------------------------------

  getLayerLabel(): string {
    const layer = this.getLayer();
    if (
      this.getTestType() === TestTypeValue.Y1564 ||
      this.getTestType() === TestTypeValue.BERT ||
      (this.getTestType() === TestTypeValue.RFC2544 && layer)
    ) {
      if (layer === TestLayerValue.L2) {
        return TestLayerLabel.L2;
      } else if (layer === TestLayerValue.L3) {
        return TestLayerLabel.L3;
      } else if (layer === TestLayerValue.L4) {
        return TestLayerLabel.L4;
      } else {
        return layer;
      }
    }
    return layer;
  }

  getModeLabel(): string {
    const testType = this.getTestType();
    if (
      testType === TestTypeValue.TRUESPEED ||
      testType === TestTypeValue.Y1564 ||
      testType === TestTypeValue.BERT ||
      testType === TestTypeValue.CONTAINER_TRUESPEED ||
      testType === TestTypeValue.UDP ||
      testType === TestTypeValue.CONTAINER_UDP ||
      testType === TestTypeValue.VOIP ||
      testType === 'ping' ||
      testType === 'traceroute' ||
      testType === TestTypeValue.HTTP ||
      testType === TestTypeValue.FTP ||
      testType === TestTypeValue.BERT
    ) {
      if (isEmpty(this.getTestListData())) {
        return '';
      } else if (this.getMode() === 'ma-to-ma') {
        return TestModeLabel.MA_TO_MA;
      } else if (this.getMode() === 'ma-to-jmep') {
        return TestModeLabel.MA_TO_JMEP;
      } else if (this.getMode() === 'client-to-ma') {
        return TestModeLabel.CLIENT_TO_MA;
      } else if (this.getMode() === 'testpoint-to-testpoint') {
        return TestModeLabel.MA_TO_SERVER;
      } else if (this.getMode() === 'testpoint-to-server') {
        return TestModeLabel.MA_TO_EXTERNAL;
      } else if (testType === TestTypeValue.CONTAINER_TRUESPEED || testType === 'container:udp') {
        return TestModeLabel.MA_TO_MA;
      } else if (testType === TestTypeValue.TRUESPEED || testType === TestTypeValue.UDP) {
        return TestModeLabel.CLIENT_TO_MA;
      }
    }
  }

  getTestType(): string {
    const testType = get(this.testListData, 'type', null);
    switch (testType) {
      case 'y1564': {
        return TestTypeValue.Y1564;
      }
      case 'bert': {
        return TestTypeValue.BERT;
      }
      case 'truespeed': {
        return TestTypeValue.TRUESPEED;
      }
      case 'twamp-initiator': {
        return TestTypeValue.TWAMP;
      }
      case 'udp': {
        return TestTypeValue.UDP;
      }
      case 'y1731':
      case 'y1731-initiator': {
        return TestTypeValue.Y1731;
      }
      case 'rfc2544': {
        return TestTypeValue.RFC2544;
      }
      case 'network-quality': {
        return TestTypeValue.NETWORK_QUALITY;
      }
      case 'SC':
      case 'servicecontinuity':
      case 'service-continuity': {
        return TestTypeValue.SERVICE_CONTINUITY;
      }
      case 'voip': {
        return TestTypeValue.VOIP;
      }
      case 'multistep': {
        return TestTypeValue.MULTISTEP;
      }
      case 'http': {
        return TestTypeValue.HTTP;
      }
      case 'ftp': {
        return TestTypeValue.FTP;
      }
      default:
        return testType;
    }
  }

  getTestParams(testId: string): string {
    const testConfig = this.getTestConfig();
    let optionValue = '';
    if (testConfig) {
      const testConfigObj = JSON.parse(testConfig);
      const primaryConfig = testConfigObj?.config?.[0]?.primaryConfig['ietf-lmap-control:lmap'];
      if (primaryConfig) {
        const testOptions = primaryConfig.schedules.schedule[0].action[0].option;
        const option = testOptions.find(opt => opt.id === testId);
        optionValue = option ? option.value : '';
      }
    }
    return optionValue;
  }
  getScheduledParams(): number {
    const testConfig = this.getTestConfig();
    let scheduleValue = 0;
    if (testConfig) {
      const testConfigObj = JSON.parse(testConfig);
      const primaryConfig = testConfigObj?.config?.[0]?.primaryConfig['ietf-lmap-control:lmap'];
      if (primaryConfig) {
        const event = primaryConfig.events.event[0];
        scheduleValue = event.periodic.interval;
      }
    }
    return scheduleValue;
  }

  getLabelForTestType(type: string): string {
    switch (type) {
      case TestTypeValue.Y1564:
        return TestTypeDisplayValue.Y1564;
      case TestTypeValue.BERT:
        return TestTypeDisplayValue.BERT;
      case TestTypeValue.TRUESPEED:
        return TestTypeDisplayValue.TRUESPEED;
      case TestTypeValue.Y1731:
        return TestTypeDisplayValue.Y1731;
      case TestTypeValue.TWAMP:
        return TestTypeDisplayValue.TWAMP;
      case TestTypeValue.TWAMP_REFLECTOR:
        return TestTypeDisplayValue.TWAMP_REFLECTOR;
      case TestTypeValue.TWAMP_LIGHT_REFLECTOR:
        return TestTypeDisplayValue.TWAMP_LIGHT_REFLECTOR;
      case TestTypeValue.RFC2544:
        return TestTypeDisplayValue.RFC2544;
      case TestTypeValue.UDP:
        return TestTypeDisplayValue.UDP;
      case TestTypeValue.SERVICE_CONTINUITY:
      case 'service-continuity':
      case 'servicecontinuity':
        return TestTypeDisplayValue.SERVICE_CONTINUITY;
      case TestTypeValue.NETWORK_QUALITY:
      case 'network-quality':
      case 'networkquality':
        return TestTypeDisplayValue.NETWORK_QUALITY;
      case TestTypeValue.VOIP:
      case 'voip':
        return TestTypeDisplayValue.VOIP;
      case TestTypeValue.MULTISTEP:
        return TestTypeDisplayValue.MULTISTEP;
      case TestTypeValue.HTTP:
        return TestTypeDisplayValue.HTTP;
      case TestTypeValue.FTP:
        return TestTypeDisplayValue.FTP;
      default:
        return type;
    }
  }

  private getScheduleDataParams() {
    let schedulerData = '';
    const testConfig = this.getTestConfig();
    if (testConfig) {
      const testConfigObj = JSON.parse(testConfig);
      const primaryConfig = testConfigObj?.config?.[0]?.primaryConfig['ietf-lmap-control:lmap'];
      if (primaryConfig) {
        schedulerData = primaryConfig.events.event[0].periodic;
      }
    }
    return schedulerData;
  }

  /*
   * Change _DESTROYED_ from given string to _EXPIRED_ for troubleshooting monitors
   */
  private changeDestroyMsg(alias: string): string {
    if (alias && this.getTestListRunType() === 'on-demand') {
      return replace(alias, '_DESTROYED_', '_EXPIRED_');
    }
    return alias;
  }
}
